import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VoicePrivacyHint } from "@/components/VoicePrivacyHint";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, Mic, MicOff, Camera, X, ImageIcon, Volume2, VolumeX } from "lucide-react";
import Mascot from "@/components/Mascot";
import { ProBadge } from "@/components/ProBadge";

// Tiny markdown renderer for coach answers: **bold**, *italic* and "- " lists
// (whitespace-pre-wrap already preserves line breaks). Keeps us dependency-free.
export function renderMarkdownLite(text) {
  if (typeof text !== "string") return text;
  return text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    if (part.length > 2 && part.startsWith("*") && part.endsWith("*")) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

const SUGGESTIONS = {
  it: [
    "Quante proteine dovrei mangiare oggi?",
    "Uno spuntino sano dopo l'allenamento?",
    "Come aumento le fibre senza carbo in più?",
  ],
  en: [
    "How much protein should I eat today?",
    "A healthy post-workout snack?",
    "How to increase fibre without more carbs?",
  ],
  de: [
    "Wie viel Protein sollte ich heute essen?",
    "Ein gesunder Snack nach dem Training?",
    "Wie erhöhe ich Ballaststoffe ohne mehr Kohlenhydrate?",
  ],
};

export default function AICoachChatDialog({ open, onOpenChange }) {
  const { t, lang } = useT();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null); // { dataUrl, thumb }
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  // Reset conversation whenever dialog opens (per user choice: no history)
  useEffect(() => {
    if (open) {
      setMessages([]);
      setInput("");
      setAttachment(null);
    } else {
      // stop any ongoing recording when closing
      stopRecording(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const compressImage = (file, maxWidth = 1280, quality = 0.8) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const pickPhoto = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      setAttachment({ dataUrl });
    } catch {
      toast.error(t("common.error"));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const stopRecording = (silent = false) => {
    try {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== "inactive") mr.stop();
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch {}
    if (!silent) setRecording(false);
    streamRef.current = null;
  };

  const startRecording = async () => {
    if (recording || transcribing || sending) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      return toast.error(t("aiCoach.micUnsupported"));
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Pick a supported MIME
      const preferredMimes = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
      let mime = "";
      for (const m of preferredMimes) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) { mime = m; break; }
      }
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        streamRef.current?.getTracks?.().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(audioChunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 800) { setRecording(false); return; }
        setTranscribing(true);
        try {
          const dataUrl = await new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.onerror = reject;
            fr.readAsDataURL(blob);
          });
          const { data } = await api.post("/client/ai-coach/transcribe", {
            audio_base64: dataUrl,
            lang: lang || null,
          });
          const text = (data?.text || "").trim();
          if (!text) throw new Error("empty");
          setInput((prev) => (prev ? `${prev} ${text}` : text));
        } catch (err) {
          toast.error(err?.response?.data?.detail || t("aiCoach.transcribeError"));
        } finally {
          setTranscribing(false);
          setRecording(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      // Safety cap: auto-stop at 30s
      setTimeout(() => { if (mediaRecorderRef.current?.state === "recording") stopRecording(); }, 30000);
    } catch (e) {
      toast.error(t("aiCoach.micDenied"));
      setRecording(false);
    }
  };

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if ((!q && !attachment) || sending) return;
    setInput("");
    const userMsg = { role: "user", content: q || (attachment ? "📷" : ""), image: attachment?.dataUrl };
    setMessages((m) => [...m, userMsg]);
    const priorHistory = messages;
    const imageToSend = attachment?.dataUrl || null;
    setAttachment(null);
    setSending(true);
    try {
      const { data } = await api.post("/client/ai-coach/ask", {
        question: q || (imageToSend ? (lang === "de" ? "Was siehst du auf diesem Foto?" : lang === "en" ? "What do you see in this photo?" : "Cosa vedi in questa foto?") : ""),
        lang: lang || "it",
        history: priorHistory,
        image_base64: imageToSend,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
      if (voiceModeRef.current) speak(data.answer);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
      setMessages((m) => m.slice(0, -1));
      setInput(q);
      if (imageToSend) setAttachment({ dataUrl: imageToSend });
    } finally { setSending(false); }
  };

  const suggestions = SUGGESTIONS[lang] || SUGGESTIONS.it;

  // Voice mode: read coach answers aloud (OpenAI TTS)
  const [voiceMode, setVoiceMode] = useState(() => localStorage.getItem("solano_coach_voice") === "1");
  const [speaking, setSpeaking] = useState(false);
  const voiceModeRef = useRef(voiceMode);
  const audioRef = useRef(null);
  const stopSpeaking = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setSpeaking(false);
  };
  const toggleVoiceMode = () => {
    const nv = !voiceMode;
    setVoiceMode(nv);
    voiceModeRef.current = nv;
    localStorage.setItem("solano_coach_voice", nv ? "1" : "0");
    if (!nv) stopSpeaking();
    else {
      const last = [...messages].reverse().find((m) => m.role === "assistant");
      if (last) speak(last.content);
    }
  };
  const speak = async (text) => {
    stopSpeaking();
    try {
      setSpeaking(true);
      const res = await api.post("/client/ai-coach/tts", { text }, { responseType: "blob" });
      if (!voiceModeRef.current) { setSpeaking(false); return; }
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      await audio.play();
    } catch {
      setSpeaking(false);
    }
  };
  useEffect(() => () => stopSpeaking(), []);
  useEffect(() => { if (!open) stopSpeaking(); }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 gap-0 overflow-hidden h-[85vh] sm:h-[600px] flex flex-col"
        data-testid="ai-coach-chat-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200 shrink-0">
          <DialogTitle className="flex items-center gap-2 font-heading">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#E8951C]/20 to-[#5A8B8F]/20 flex items-center justify-center overflow-hidden">
              <Mascot size={32} pose="attentive" alt="Solano" />
            </div>
            <span className="flex-1 flex items-center gap-2">{t("aiCoach.chatTitle")} <ProBadge /></span>
            <button
              type="button"
              onClick={toggleVoiceMode}
              data-testid="ai-coach-voice-toggle"
              title={voiceMode ? t("aiCoach.voiceOff") : t("aiCoach.voiceOn")}
              className={`h-9 w-9 mr-5 rounded-full flex items-center justify-center transition-all ${
                voiceMode
                  ? "bg-[#E8951C] text-white shadow-md" + (speaking ? " animate-pulse" : "")
                  : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300"
              }`}
            >
              {voiceMode ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
          </DialogTitle>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-slate-500">{t("aiCoach.chatHint")}</p>
            <VoicePrivacyHint testId="voice-privacy-coach" className="shrink-0" />
          </div>
        </DialogHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="ai-coach-chat-scroll">
          {messages.length === 0 && !sending && (
            <div>
              <p className="text-sm text-slate-500 mb-3">{t("aiCoach.suggestionsLabel")}</p>
              <div className="flex flex-col gap-2">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => ask(s)}
                    className="text-left text-sm rounded-xl border border-slate-200 bg-white hover:bg-slate-50 px-3 py-2 transition"
                    data-testid={`ai-coach-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`ai-coach-msg-${m.role}-${i}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-snug whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#5A8B8F] text-white rounded-tr-sm"
                    : "bg-slate-100 text-[#1A1D1E] rounded-tl-sm"
                }`}
              >
                {m.image && (
                  <img
                    src={m.image}
                    alt="attachment"
                    className="rounded-lg mb-1.5 max-h-40 w-auto"
                    data-testid={`ai-coach-msg-image-${i}`}
                  />
                )}
                {m.role === "assistant" ? renderMarkdownLite(m.content) : m.content}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex items-end gap-2 justify-start" data-testid="ai-coach-typing">
              <Mascot size={32} pose="analyzing" className="shrink-0" />
              <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-3 py-2 flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin text-[#5A8B8F]" />
                <span className="text-xs text-slate-500">{t("aiCoach.thinking")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-slate-200 shrink-0 bg-white">
          {attachment && (
            <div className="flex items-center gap-2 px-3 pt-2" data-testid="ai-coach-attachment">
              <div className="relative">
                <img src={attachment.dataUrl} alt="preview" className="h-14 w-14 rounded-lg object-cover" />
                <button
                  type="button"
                  onClick={() => setAttachment(null)}
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white shadow border border-slate-200 flex items-center justify-center"
                  aria-label={t("common.close")}
                  data-testid="ai-coach-attachment-remove"
                >
                  <X className="h-3 w-3 text-slate-700" />
                </button>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <ImageIcon className="h-3 w-3" /> {t("aiCoach.photoReady")}
              </p>
            </div>
          )}

          <div className="p-3 flex items-end gap-2">
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={sending || recording || transcribing}
              data-testid="ai-coach-photo-btn"
              className="h-10 w-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-40 shrink-0 transition"
              aria-label={t("aiCoach.takePhoto")}
              title={t("aiCoach.takePhoto")}
            >
              <Camera className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || recording || transcribing}
              data-testid="ai-coach-gallery-btn"
              className="h-10 w-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-40 shrink-0 transition"
              aria-label={t("aiCoach.fromGallery")}
              title={t("aiCoach.fromGallery")}
            >
              <ImageIcon className="h-4 w-4" />
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="ai-coach-camera-input"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="ai-coach-photo-input"
            />

            <button
              type="button"
              onClick={recording ? () => stopRecording() : startRecording}
              disabled={sending || transcribing}
              data-testid="ai-coach-mic-btn"
              className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 transition disabled:opacity-40 ${
                recording ? "bg-red-500 text-white animate-pulse" : "bg-slate-100 hover:bg-slate-200 text-slate-600"
              }`}
              aria-label={recording ? t("aiCoach.stopRec") : t("aiCoach.startRec")}
              title={recording ? t("aiCoach.stopRec") : t("aiCoach.startRec")}
            >
              {transcribing
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : recording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>

            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); }
              }}
              placeholder={
                recording ? t("aiCoach.recording") :
                transcribing ? t("aiCoach.transcribing") :
                t("aiCoach.inputPlaceholder")
              }
              disabled={sending || recording || transcribing}
              data-testid="ai-coach-chat-input"
              className="flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#5A8B8F] focus:ring-1 focus:ring-[#5A8B8F] max-h-24"
            />
            <Button
              onClick={() => ask()}
              disabled={sending || recording || transcribing || (!input.trim() && !attachment)}
              data-testid="ai-coach-chat-send"
              className="rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white h-10 w-10 p-0 shrink-0"
              aria-label={t("aiCoach.send")}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
